

































































































































import { Vue, Component, Prop, Ref, Watch } from "vue-property-decorator";
import IresDeviceStatusComponent from "@/components/devices/IresDeviceStatus.vue";
import QueuedRequestComponent from "@/components/panel/QueuedRequest.vue";
import PanelStatusCardEntryComponent from "@/components/panel/PanelStatusEntry.vue";
import PanelControlComponent from "@/components/panel/PanelControl.vue";
import ComBoxControlComponent from "@/components/combox/ComBoxControl.vue";
import {
  ProgramChain,
  getMeterReadingInfo,
  isOverviewProgramChain,
  isNewDryRunProgramChain,
  getProgramChainDurationDescription,
} from "@/data/models/ProgramChain";

import {
  getLastUpdateText,
  isActiveProgramChainOfIresDevice,
  isPoweredOff,
} from "@/data/models/IresDevice";

import {
  formatFloat,
  formatKWh,
  joinToString,
} from "@/utils/LocalisationHelper";
import ComBoxSettingsComponent from "@/components/combox/ComBoxSettings.vue";
import { resolveClientVisibleComBoxEvent } from "@/data/models/HyDryEvent";
import { isIdle } from "@/data/models/IresDevice";
import {
  ComBox,
  getComBoxProgramName,
  getComBoxDeviceStateDescription,
  getCurrentSensorData,
  getLidStateDescription,
  isRoomDryingProgram,
  isLidOpen,
  isNightModeActive,
  getComBoxErrorFlagDescriptions,
  getComBoxErrorFlags,
  getGroupedComBoxErrorStateFlags,
  getComBoxState,
  ComBoxDeviceState,
  isDryRunActiveOrInterrupted,
  isMonitorProgramOfComBox,
  formatActiveDryingPauseType,
  isHyDryMini,
} from "@/data/models/ComBox";
import { formatResistanceDigits } from "@/data/models/ComBoxSensorData";
import {
  formatComBoxSensorData,
  getFloorDisplaySensorIndex,
  getMaterialDisplaySensorIndex,
  formatSingleSensorData,
} from "@/utils/SensorData";
import { ComBoxSampleInfo } from "@/data/models/SampleInfo";
import { ProgramChainAssignmentInfo } from "../devices/IresDeviceDetail.vue";
import StateButtonComponent, { ButtonState } from "../common/StateButton.vue";
import { 
  controlComBoxViaIotAccess,
  showAllInfoAccess,
  showOpenSettingsIconAccess,
  showAdditionalDeviceInfoAccess  
} from "@/permissions/UserPermissions";
import { COLOR_BLACK_TEXT, COLOR_BLACK_TEXT_DARK, COLOR_ERROR_TEXT, COLOR_ERROR_TEXT_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { hasCapability, DeviceCapability } from "@/data/models/DeviceCapability";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "state-button": StateButtonComponent,
    "queued-request": QueuedRequestComponent,
    "ires-device-status": IresDeviceStatusComponent,
    "panel-status-card-entry": PanelStatusCardEntryComponent,
    "panel-control": PanelControlComponent,
    "com-box-control": ComBoxControlComponent,
    "com-box-settings": ComBoxSettingsComponent,
  },
})
export default class ComBoxStatusCardComponent extends Vue {
  @Prop() lastRefreshDate!: Date;

  @Prop() selectedProgramChain!: ProgramChain;
  @Prop() selectedProgramChainDescription!: string;

  @Prop() showLatestIresDeviceInfo!: boolean;
  @Prop() comBox!: ComBox;

  @Prop() programChainAssignmentInfo!: ProgramChainAssignmentInfo | null;
  @Prop({ default: false }) isLoading!: boolean;

  @Prop() isCurrentlyAssignedToProject!: boolean;
  @Prop({ default: true }) showIresDeviceControl!: boolean;
  @Prop({ default: false }) showDryingHistoryButton!: boolean;
  @Prop({ default: false }) showNoActiveProgramChainWarning!: boolean;

  @Prop() programChainNumberLookup!: Map<number, number>;

  @Prop({ default: () => new Map() }) sensorLegendMetadata!: Map<number, { serialNumber?: string }>;

  @Ref("comBoxSettings")
  comBoxSettingsComponent!: ComBoxSettingsComponent;

  @Watch("selectedProgramChain", { immediate: true })
  onSelectedProgramChainChanged() {
    this.computeAdjustedProgramChainData();
  }

  isDisplayingGraph = false;

  showDryingHistoryButtonState = ButtonState.RED;

  programChainDurationDescription: string | null = null;

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewSettingsIcon() {
    return showOpenSettingsIconAccess(this.activeUser);
  }

  get isAllowedToViewAdditionalInfo() {
    return showAdditionalDeviceInfoAccess(this.activeUser);
  }

  get canCurrentUserControlComBox() {
    return controlComBoxViaIotAccess(this.activeUser);
  }

  get currentSensorData() {
    return getCurrentSensorData(this.comBoxStatus);
  }

  get roomTempHumidity() {
    return this.formatRoomTempAndHumidity();
  }

  get floorDisplaySensorIndex(): number | null {
    return getFloorDisplaySensorIndex(this.comBoxStatus);
  }

  get floorTempHumidity() {
    const sensorIndex = this.floorDisplaySensorIndex;

    let floorData = "-";

    if (sensorIndex !== null) {
      const isIdle = getComBoxState(this.comBox) === ComBoxDeviceState.IDLE;

      const statusToDisplay = isIdle
        ? this.comBoxStatus
        : this.showLatestIresDeviceInfo
          ? this.selectedProgramChain
            ? this.comBoxStatusOfActiveDryRun
            : this.comBoxStatus
          : undefined;

      const programChainToDisplay = isIdle
        ? undefined
        : this.selectedProgramChain;

      floorData = formatSingleSensorData(
        this,
        sensorIndex,
        statusToDisplay,
        programChainToDisplay?.runs
      );
    }

    // Material sensors have their own row only in the graph legend; in the compact
    // status card the resistance is appended to the floor field (a shared row fits
    // only four columns), so the label reads "Temp / Feuchte / Digits". Only append
    // it when an actual reading exists — otherwise the "Digits" label would hang
    // over an empty "-" and read as a missing value rather than "not applicable".
    if (this.hasMaterialResistanceValue) {
      return `${floorData} / ${this.materialResistance}`;
    }

    return floorData;
  }

  get floorTempHumidityLabel() {
    const isMini = isHyDryMini(this.comBox);

    const baseLabel = this.$t(
      isMini ? "comBoxDetail.tempHumidity" : "comBoxDetail.floorTempHumidity"
    ).toString();

    if (this.hasMaterialResistanceValue) {
      return `${baseLabel} / ${this.$t("dryingGraph.resistanceDigitsAxisLabel")}`;
    }

    return baseLabel;
  }

  /**
   * Raw resistance-digits reading of the selected material sensor, or `undefined`
   * when the device has no material sensor or that sensor has no reading yet.
   */
  get materialResistanceDigits(): number | undefined {
    const sensorIndex = getMaterialDisplaySensorIndex(this.comBoxStatus);
    if (sensorIndex === null) return undefined;

    return getCurrentSensorData(this.comBoxStatus)[sensorIndex]?.resistanceDigits;
  }

  /**
   * Whether a valid material resistance reading is available. The "Digits"
   * label and value are only shown when this is true, so a missing/incomplete
   * reading doesn't leave a misleading "Digits: -" in the floor field.
   */
  get hasMaterialResistanceValue(): boolean {
    return this.materialResistanceDigits !== undefined;
  }

  /** Resistance-digits reading of the selected material sensor, or "-" when unavailable. */
  get materialResistance(): string {
    return formatResistanceDigits(this, this.materialResistanceDigits);
  }

  formatRoomTempAndHumidity() {
    const isIdle = getComBoxState(this.comBox) === ComBoxDeviceState.IDLE;

    const programChainToDisplay = isIdle
      ? undefined
      : this.selectedProgramChain;

    const statusToDisplay = isIdle
      ? this.comBoxStatus
      : this.showLatestIresDeviceInfo
        ? this.selectedProgramChain
          ? this.comBoxStatusOfActiveDryRun
          : this.comBoxStatus
        : undefined;

    return formatComBoxSensorData(
      this,
      true,
      statusToDisplay,
      programChainToDisplay
    );
  }

  get meterReadingInfoStartEnd() {
    const meterReadingInfo = getMeterReadingInfo(this.selectedProgramChain);

    if (!meterReadingInfo) {
      return null;
    }

    return this.$t("panelDetail.metereReadingStartEnd", [
      formatFloat(meterReadingInfo.initialMeterReading, 1),
      formatFloat(meterReadingInfo.lastMeterReading, 1),
    ]).toString();
  }

  get meterReadingInfoPowerConsumed() {
    const meterReadingInfo = getMeterReadingInfo(this.selectedProgramChain);

    if (!meterReadingInfo) {
      return null;
    }

    return formatKWh(this, meterReadingInfo.totalPowerConsumed, 1);
  }

  get comBoxStatusOfActiveDryRun() {
    if (
      isActiveProgramChainOfIresDevice(this.comBox, this.selectedProgramChain)
    ) {
      return this.comBoxStatus;
    } else if (getComBoxState(this.comBox) === ComBoxDeviceState.IDLE) {
      return this.comBoxStatus;
    } else {
      return undefined;
    }
  }

  get durationLabel() {
    if (!this.duration) {
      return "";
    }

    if (this.showLatestIresDeviceInfo && this.isDryRunActiveOrInterrupted) {
      return this.$t("panelDetail.runningSince");
    } else {
      return this.$t("panelDetail.runTime");
    }
  }

  get duration() {
    return this.programChainDurationDescription;
  }

  get noInfoAvailable() {
    return false;
  }

  get latestPanelInfoStyle() {
    return `visibility : ${this.showLatestIresDeviceInfo ? "visible" : "hidden"
      }`;
  }

  get isComBoxIdle(): boolean {
    return isIdle(this.comBox);
  }

  get startStopButtonText(): string {
    return this.$t(
      this.isComBoxIdle
        ? "panelDetail.actions.startPanel"
        : "panelDetail.actions.stopPanel"
    ).toString();
  }

  get comBoxStatus() {
    return this.comBox.comBoxStatus;
  }

  get isDryRunActiveOrInterrupted() {
    return isDryRunActiveOrInterrupted(this.comBox);
  }

  get dryingProgramTextColor() {
    return this.showNoActiveProgramChainWarning ? themed(COLOR_ERROR_TEXT, COLOR_ERROR_TEXT_DARK) : themed(COLOR_BLACK_TEXT, COLOR_BLACK_TEXT_DARK);
  }

  get dryingProgram() {
    if (this.showNoActiveProgramChainWarning) {
      return this.$t("panelDetail.noActiveProgramChainWarning");
    }

    const programChain = this.selectedProgramChain;

    if (!programChain) {
      return "-";
    }

    if (isNewDryRunProgramChain(programChain)) {
      return this.$t("panelDetail.dryingProgressGraphControl.new");
    }

    if (isOverviewProgramChain(programChain)) {
      return "";
    }

    const firstRun = programChain.runs[0];
    const firstRunProgramName = getComBoxProgramName(
      this,
      firstRun.programNumber,
      this.comBox
    );

    if (isMonitorProgramOfComBox(firstRun.programNumber)) {
      return firstRunProgramName;
    }

    const lastRun = programChain.runs[programChain.runs.length - 1];

    const lastRunProgramName = getComBoxProgramName(
      this,
      lastRun.programNumber,
      this.comBox
    );

    if (firstRun.programNumber === lastRun.programNumber ||
      lastRun.programNumber < 4
    ) {
      const latestSample = lastRun.samples[
        lastRun.samples.length - 1
      ] as ComBoxSampleInfo;

      if (isRoomDryingProgram(firstRun.programNumber)) {
        return lastRunProgramName;
      }
      else {
        return `${lastRunProgramName} / ${this.$t("comBoxDetail.formatPhase", [
          (latestSample?.step ?? 0) + 1,
        ])}`;
      }
    }

    return `${firstRunProgramName} / ${lastRunProgramName}`;
  }

  get dryingPauseDescription() {
    const dryingPauseType = this.comBoxStatus.dryingPauseType;

    return formatActiveDryingPauseType(this, dryingPauseType);
  }

  get nightModeDescription() {
    if (isNightModeActive(this.comBoxStatus)) {
      return this.$t("comBox.nightModeActive").toString();
    } else {
      return null;
    }
  }

  get deviceStateDescription() {
    return getComBoxDeviceStateDescription(this, this.comBox);
  }

  get lidStateDescription() {
    if (isPoweredOff(this.comBox)) {
      return null;
    }

    return isLidOpen(this.comBox)
      ? getLidStateDescription(this, this.comBox)
      : null;
  }

  get programStepCycle() {
    const comBoxStatus = this.comBoxStatus;

    if (!comBoxStatus) {
      return null;
    }

    return `P${comBoxStatus.activeProgram ?? 0}/S${comBoxStatus.step
      }/Z${comBoxStatus.cycle ?? 0}`;
  }

  get numRuns() {
    const selectedProgramChain = this.selectedProgramChain;

    if (!selectedProgramChain) {
      return "";
    }

    return selectedProgramChain.runs.length.toString();
  }

  get heatingInfo() {
    return "";
  }

  get event() {
    if (this.comBoxStatus.lastEvent) {
      return this.comBoxStatus.lastEvent;
    } else {
      return "-";
    }
  }

  get connectReason() {
    const connectReason = this.comBoxStatus.connectReason;

    if (!connectReason || connectReason < 1 || connectReason === 255) {
      return "";
    }

    const resourceId = `comBox.events.event${connectReason}`;
    const resolved = this.$t(resourceId);

    return resolved === resourceId ? connectReason.toString() : resolved;
  }

  get lastUpdate() {
    const comBoxStatus = this.comBoxStatus;

    if (!comBoxStatus) {
      return "";
    }

    return getLastUpdateText(this, this.lastRefreshDate, comBoxStatus, true);
  }

  // ==================== FIRMWARE v4.0.0+ CHANGES ====================
  get doesSupportExtendedSensors() {
    return hasCapability(this.comBox, DeviceCapability.EXTENDED_SENSORS);
  }

  get errorState(): string | undefined {
    const errorFlags = getGroupedComBoxErrorStateFlags(this.comBoxStatus, this.doesSupportExtendedSensors);
    const showAllInfo = showAllInfoAccess(this.activeUser);

    const result = joinToString(
      [
        this.formatErrorStateFlags(
          "comBox.formatComBoxError",
          errorFlags.errorFlags,
          showAllInfo
        ),
        this.formatErrorStateFlags(
          "comBox.formatComBoxWarning",
          errorFlags.warningFlags,
          showAllInfo
        ),
        showAllInfo
          ? this.formatErrorStateFlags(
            "comBox.formatComBoxInfo",
            errorFlags.infoFlags,
            showAllInfo
          )
          : undefined,
      ],
      "\n"
    );

    if (!result) {
      return undefined;
    }

    return result;
  }

  created() {
    this.computeAdjustedProgramChainData();
  }

  async computeAdjustedProgramChainData() {
    const programChain = this.selectedProgramChain;

    if (programChain && isNewDryRunProgramChain(programChain)) {
      this.programChainDurationDescription = "-";
    }

    if (!programChain) {
      return null;
    }

    const openEnded =
      this.showLatestIresDeviceInfo &&
      isActiveProgramChainOfIresDevice(this.comBox, programChain);

    this.programChainDurationDescription = await getProgramChainDurationDescription(
      this,
      programChain,
      openEnded
    );
  }

  formatErrorStateFlags(
    formatI18n: string,
    errorFlags: number[],
    showFlagNumber: boolean
  ) {
    const errorDescriptions = getComBoxErrorFlagDescriptions(this, errorFlags, {
      showFlagNumber,
      // ==================== FIRMWARE v4.0.0+ CHANGES ====================
      doesSupportExtendedSensors: this.doesSupportExtendedSensors
    });

    if (!errorDescriptions) {
      return undefined;
    }

    return this.$t(formatI18n, [errorDescriptions]).toString();
  }

  get floorTempHumidityTextColor() {
    // ==================== FIRMWARE v4.0.0+ CHANGES ====================
    const errorFlags = getComBoxErrorFlags(this.comBoxStatus, undefined, this.doesSupportExtendedSensors);

    return errorFlags.indexOf(7) >= 0 ? themed(COLOR_ERROR_TEXT, COLOR_ERROR_TEXT_DARK) : themed(COLOR_BLACK_TEXT, COLOR_BLACK_TEXT_DARK);
  }

  get hideLastEventDetails() {
    return this.lastEventText === this.$t("comBox.events.event28").toString();
  }

  /**
   * The client's "Letztes Ereignis" number. See {@link resolveClientVisibleComBoxEvent} for why a
   * missing `lastClientEvent` falls back to the raw event rather than showing nothing.
   */
  get clientVisibleLastEvent() {
    return this.comBoxStatus
      ? resolveClientVisibleComBoxEvent(this.comBoxStatus)
      : undefined;
  }

  /**
   * "Letztes Ereignis". IRES reads the raw device event; a client reads `lastClientEvent`, the
   * newest event the Cloud considered client-safe (see `isComBoxEventVisibleToClients` there). The
   * two fields exist so that hiding an event from a client never hides it from IRES.
   */
  get lastEventText() {
    const lastEvent = this.isAllowedToViewAdditionalInfo
      ? this.comBoxStatus?.lastEvent
      : this.clientVisibleLastEvent;

    if (!lastEvent || lastEvent === 255) {
      return "";
    }

    if (lastEvent == 28) {
      // ==================== FIRMWARE v4.0.0+ CHANGES ====================
      const errors = getComBoxErrorFlags(this.comBoxStatus, undefined, this.doesSupportExtendedSensors);

      if (errors.indexOf(30) < 0) {
        return this.$t("comBox.events.warningFixed").toString();
      }
    }

    const resourceId = `comBox.events.event${lastEvent}`;
    const resolved = this.$t(resourceId);

    return resolved === resourceId ? lastEvent.toString() : resolved;
  }

  onOpenSettings() {
    this.comBoxSettingsComponent.show();
  }

  showDryingHistory() {
    this.$emit("showDryingHistory");
  }
}
